import { useState, useEffect, useCallback, useRef } from 'react';
import { usePortalStore } from '@/store/portal-store.js';
import { makePortalInstance, type Finding } from '@edison/api-client-react';
import { MessageSquare, Plus, Pencil, Trash2, Check, X, AlertCircle } from 'lucide-react';
import { InlineError } from '@/components/ui/inline-error.js';

// DB values stay as info/concern/critical; labels are for display only
const SEVERITY_CONFIG = {
  info:     { label: 'Observation', description: 'Noted for context — no action required', class: 'bg-gray-800 text-gray-300 border border-gray-700' },
  concern:  { label: 'Query',       description: 'Needs clarification or explanation',     class: 'bg-amber-950/60 text-amber-300 border border-amber-900/60' },
  critical: { label: 'Issue',       description: 'Requires resolution before lodgement',   class: 'bg-red-950/60 text-red-300 border border-red-900/60' },
} as const;

type SeverityKey = keyof typeof SEVERITY_CONFIG;

function SeverityBadge({ severity }: { severity: string | null }) {
  const cfg = SEVERITY_CONFIG[(severity ?? 'info') as SeverityKey] ?? SEVERITY_CONFIG.info;
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${cfg.class}`}>
      {cfg.label}
    </span>
  );
}

// ── Single finding card ───────────────────────────────────────────────────────

interface FindingCardProps {
  finding: Finding;
  onUpdated: (updated: Finding) => void;
  onDeleted: (id: string) => void;
  portal: ReturnType<typeof makePortalInstance>;
}

function FindingCard({ finding, onUpdated, onDeleted, portal }: FindingCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(finding.content);
  const [editSeverity, setEditSeverity] = useState<SeverityKey>((finding.severity ?? 'info') as SeverityKey);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) textareaRef.current?.focus();
  }, [isEditing]);

  const handleSave = async () => {
    if (!editContent.trim()) return;
    setIsSaving(true);
    setError('');
    try {
      type PatchResp = { data: Finding };
      const res = await portal.patch<PatchResp>(`/api/portal/findings/${finding.id}`, {
        content: editContent.trim(),
        severity: editSeverity,
      });
      onUpdated(res.data);
      setIsEditing(false);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');
    try {
      await portal.delete(`/api/portal/findings/${finding.id}`);
      onDeleted(finding.id);
    } catch {
      setError('Failed to delete. Please try again.');
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(finding.content);
    setEditSeverity((finding.severity ?? 'info') as SeverityKey);
    setError('');
  };

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityBadge severity={isEditing ? editSeverity : (finding.severity ?? null)} />
          <span className="text-xs text-gray-600">
            {new Date(finding.createdAt).toLocaleDateString('en-AU', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </span>
        </div>

        {!isEditing && !confirmDelete && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setIsEditing(true)}
              className="rounded p-1.5 text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors"
              aria-label="Edit finding"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="rounded p-1.5 text-gray-600 hover:text-red-400 hover:bg-gray-800 transition-colors"
              aria-label="Delete finding"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Severity</label>
            <div className="flex gap-2 flex-wrap">
              {(Object.keys(SEVERITY_CONFIG) as SeverityKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setEditSeverity(key)}
                  className={`rounded px-2.5 py-1 text-xs font-medium border transition-colors ${
                    editSeverity === key
                      ? SEVERITY_CONFIG[key].class + ' ring-1 ring-offset-1 ring-offset-gray-900 ring-current'
                      : 'bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300'
                  }`}
                >
                  {SEVERITY_CONFIG[key].label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-600">{SEVERITY_CONFIG[editSeverity].description}</p>
          </div>
          <textarea
            ref={textareaRef}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={4}
            className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={() => void handleSave()}
              disabled={isSaving || !editContent.trim()}
              className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
            >
              <Check className="h-3.5 w-3.5" />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-1.5 rounded border border-gray-700 px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-800 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        </div>
      ) : confirmDelete ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-300">Remove this finding? This cannot be undone.</p>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={() => void handleDelete()}
              disabled={isDeleting}
              className="inline-flex items-center gap-1.5 rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {isDeleting ? 'Removing…' : 'Remove finding'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="rounded border border-gray-700 px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-800 transition-colors"
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{finding.content}</p>
      )}
    </div>
  );
}

// ── New finding form ──────────────────────────────────────────────────────────

interface NewFindingFormProps {
  onCreated: (finding: Finding) => void;
  onCancel: () => void;
  portal: ReturnType<typeof makePortalInstance>;
}

function NewFindingForm({ onCreated, onCancel, portal }: NewFindingFormProps) {
  const [content, setContent] = useState('');
  const [severity, setSeverity] = useState<SeverityKey>('info');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      type CreateResp = { data: Finding };
      const res = await portal.post<CreateResp>('/api/portal/findings', { content: content.trim(), severity });
      onCreated(res.data);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="rounded-lg border border-gray-700 bg-gray-900 p-4 space-y-4">
      <p className="text-sm font-medium text-white">New finding</p>

      <div>
        <label className="block text-xs text-gray-500 mb-1.5">Type</label>
        <div className="flex gap-2 flex-wrap">
          {(Object.keys(SEVERITY_CONFIG) as SeverityKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setSeverity(key)}
              className={`rounded px-2.5 py-1 text-xs font-medium border transition-colors ${
                severity === key
                  ? SEVERITY_CONFIG[key].class + ' ring-1 ring-offset-1 ring-offset-gray-900 ring-current'
                  : 'bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300'
              }`}
            >
              {SEVERITY_CONFIG[key].label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-600">{SEVERITY_CONFIG[severity].description}</p>
      </div>

      <div>
        <label htmlFor="finding-content" className="block text-xs text-gray-500 mb-1.5">
          Finding <span className="text-red-500">*</span>
        </label>
        <textarea
          id="finding-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Describe the finding or observation in plain language…"
          rows={4}
          autoFocus
          className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting || !content.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? 'Submitting…' : 'Submit finding'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-700 px-4 py-2 text-xs text-gray-400 hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PortalFindingsPage() {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const portal = makePortalInstance(token!, { mode });

  const loadFindings = useCallback(() => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    makePortalInstance(token, { mode })
      .get<{ data: { items: Finding[] } }>('/api/portal/findings')
      .then((res) => setFindings(res.data.items))
      .catch(() => setError('Failed to load findings.'))
      .finally(() => setIsLoading(false));
  }, [token, mode]);

  useEffect(() => { loadFindings(); }, [loadFindings]);

  const handleCreated = (finding: Finding) => {
    setFindings((prev) => [finding, ...prev]);
    setShowForm(false);
  };

  const handleUpdated = (updated: Finding) => {
    setFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleDeleted = (id: string) => {
    setFindings((prev) => prev.filter((f) => f.id !== id));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-7 w-48 rounded bg-gray-800 animate-pulse" />
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-24 w-full rounded bg-gray-800 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return <InlineError message={error} onRetry={loadFindings} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Findings</h1>
          <p className="text-sm text-gray-400 mt-1">
            Record observations, queries, and issues about this R&D claim.
          </p>
          {findings.length > 0 && (
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              {(['info', 'concern', 'critical'] as SeverityKey[]).map((s) => {
                const count = findings.filter((f) => (f.severity ?? 'info') === s).length;
                if (count === 0) return null;
                return (
                  <span key={s} className="flex items-center gap-1">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${SEVERITY_CONFIG[s].class}`}>
                      {SEVERITY_CONFIG[s].label}
                    </span>
                    {count}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New finding
          </button>
        )}
      </div>

      {showForm && (
        <NewFindingForm
          portal={portal}
          onCreated={handleCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {findings.length === 0 && !showForm ? (
        <div className="text-center py-12 rounded-lg border border-gray-800 border-dashed">
          <MessageSquare className="h-8 w-8 text-gray-600 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-gray-400">No findings submitted yet.</p>
          <p className="text-xs text-gray-600 mt-1">Use findings to record observations, queries, or issues for the claim owner to address.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {findings.map((f) => (
            <FindingCard
              key={f.id}
              finding={f}
              portal={portal}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}
